import type { ChangeEvent } from 'react';

import { Heading, Paragraph, SelectionInputLayout, Toggle } from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';

import {
  useGetLsproDefaultSettingsQuery,
  useUpdateLsproDefaultSettingsMutation,
} from '../../../../generated/graphql-manager';
import { useCurrentUser } from '../../../hooks/current-user';
import managerStyles from '../../../manager.module.css';

import styles from './settings-page.module.css';

export function SettingsPage() {
  const { lsproId } = useCurrentUser();
  const { t } = useTranslation();

  const { data } = useGetLsproDefaultSettingsQuery({ variables: { lsproId } });

  const defaultSettings = data?.lsproSettings?.defaultSettings;

  const [updateLsproDefaultSettings] = useUpdateLsproDefaultSettingsMutation();

  const handleDefaultSettingsChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    await updateLsproDefaultSettings({
      variables: {
        input: {
          lsproId,
          [name]: checked,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateLiveStreamProDefaultSettings: {
          __typename: 'UpdateLiveStreamProDefaultSettingsSuccess',
          defaultSettings: {
            __typename: 'LiveStreamProDefaultSettings',
            ...defaultSettings,
            ingestAuthenticationAsDefault: defaultSettings?.ingestAuthenticationAsDefault ?? false,
            privateEventAsDefault: defaultSettings?.privateEventAsDefault ?? false,
            singleSignOnAsDefault: defaultSettings?.singleSignOnAsDefault ?? false,
            [name]: checked,
          },
        },
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'LiveStreamProSettings', lsproId }),
          fields: {
            defaultSettings(existingDefaultSettings = {}) {
              return {
                ...existingDefaultSettings,
                [name]: checked,
              };
            },
          },
        });
      },
    });
  };

  return (
    <main className={managerStyles.main} data-testid="settings-page">
      <Heading className={managerStyles.grayText} data-testid="settings-heading">
        {t('manager.channelSettings.settings.heading')}
      </Heading>

      <Paragraph data-testid="settings-paragraph">{t('manager.channelSettings.settings.paragraph')}</Paragraph>

      <div className={styles.wrapper}>
        <SelectionInputLayout
          hintMessage={t('manager.channelSettings.settings.sso.description')}
          checked={defaultSettings?.singleSignOnAsDefault}
          variant="ghost"
          inputElement={
            <Toggle
              data-testid="default-settings-sso-toggle"
              name="singleSignOnAsDefault"
              checked={defaultSettings?.singleSignOnAsDefault}
              onChange={handleDefaultSettingsChange}
            >
              {t('manager.channelSettings.settings.sso.label')}
            </Toggle>
          }
        />

        <SelectionInputLayout
          hintMessage={t('manager.channelSettings.settings.privetEvent.description')}
          checked={defaultSettings?.privateEventAsDefault}
          variant="ghost"
          inputElement={
            <Toggle
              data-testid="default-settings-private-event-toggle"
              name="privateEventAsDefault"
              checked={defaultSettings?.privateEventAsDefault}
              onChange={handleDefaultSettingsChange}
            >
              {t('manager.channelSettings.settings.privetEvent.label')}
            </Toggle>
          }
        />

        <SelectionInputLayout
          hintMessage={t('manager.channelSettings.settings.ingestAuthentication.description')}
          checked={defaultSettings?.ingestAuthenticationAsDefault}
          variant="ghost"
          inputElement={
            <Toggle
              data-testid="default-settings-ingest-authentication-toggle"
              name="ingestAuthenticationAsDefault"
              checked={defaultSettings?.ingestAuthenticationAsDefault}
              onChange={handleDefaultSettingsChange}
            >
              {t('manager.channelSettings.settings.ingestAuthentication.label')}
            </Toggle>
          }
        />
      </div>
    </main>
  );
}
